import i18n from '../../config/i18n';
import store from '../../config/store';
import { waitFor } from '../../lib/wait-for';
import { isInMSTeams } from '../../lib/teams-helper';
import { hasProgramPathPrefix } from '../../config/program-basename';

// Anchored at the route root rather than matched anywhere in the path, because
// slugs allow underscores: a program slugged `homepage_preview` sits in the
// basename and would otherwise lose all telemetry. A *brand* slugged that way
// is still suppressed for events raised before program_path reaches the store,
// since there is nothing to strip yet.
const PREVIEW_ROUTE = /^\/homepage_preview(\/|$)/;
// MS Teams mobile is served from /mft + the program path, so the segment sits
// in front of the basename, not inside the route.
const TEAMS_PREFIX = /^\/mft(?=\/|$)/;

/** The route path, with everything the route table never sees removed. */
const routePath = (pathname, programPath) => {
  const withoutTeams = pathname.replace(TEAMS_PREFIX, '');

  return programPath && hasProgramPathPrefix(withoutTeams, programPath)
    ? withoutTeams.slice(programPath.length)
    : withoutTeams;
};

// Read live at emit time: a cached answer would outlive the route. An unknown
// basename leaves the path unstripped, which the anchored match then reads as a
// member page — so uncertainty emits, never suppresses.
export const isPreviewing = () => {
  if (typeof window === 'undefined') return false;

  const programPath = store.getState()?.appConfig?.program_path;

  return PREVIEW_ROUTE.test(routePath(window.location.pathname, programPath));
};

// afterTrack runs synchronously right after window.analytics.track, once the
// library has snapshotted the live URL into context — the only safe moment to
// mutate the query string a caller needs on the event.
export const track = (name, traits = {}, afterTrack) => {
  // Skip analytics in test environment where window is not defined
  if (typeof window === 'undefined') {
    return;
  }

  if (isPreviewing()) {
    return;
  }

  const state = store.getState();
  const user_id = state.programMembership && state.programMembership.id;

  traits = {
    ...traits,
    locale: i18n.language,
    user_id,
  };

  if (isInMSTeams) {
    traits.platform = 'ms_teams';
  }

  const send = () => {
    window.analytics.track(name, traits);
    if (afterTrack) afterTrack();
  };

  if (!window.analytics) {
    waitFor(
      () => Object.prototype.hasOwnProperty.call(window, 'analytics'),
      2000
    )
      .then(() => {
        window.analytics.ready(send);
      })
      .catch(() => {
        console.error(`Could not track analytics event: ${name} `, traits);
      });
    return;
  }

  window.analytics.ready(send);
};

// A function declaration, not a const: config/store applies this as middleware
// at module-evaluation time, and the two modules import each other, so only a
// hoisted binding is guaranteed to be defined whichever side is entered first.
export function trackAction() {
  return (next) => (action) => {
    if (!action.meta || !action.meta.analytics) return next(action);

    const { name, traits } = action.meta.analytics;

    track(name, traits);

    return next(action);
  };
}

export const trackable = (action, name, traits = {}) => {
  const analytics = { name, traits };

  return name
    ? {
        ...action,
        meta: { analytics },
      }
    : action;
};
